import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button, Icon, Input, plural } from './ui';
import { AuthLayout, AuthHeading, PasswordInput, FieldNote, cleanMessage } from './AuthShell';
import './Login.css';

const ASIDE = {
  art: 'cards',
  title: 'Учите слова, которые нужны именно вам',
  points: ['Своя библиотека слов и фраз', 'Повторение ровно тогда, когда пора', 'Рейтинг с друзьями для мотивации']
};

/**
 * Заголовок RateLimit (express-rate-limit, draft-7): «limit=20, remaining=17, reset=845».
 * Лимит общий на вход/регистрацию/восстановление с одного IP; в dev и тестах выключен — заголовка нет.
 */
const parseRateLimit = (headers) => {
  const raw = headers && (headers.ratelimit || headers['RateLimit']);
  if (!raw || typeof raw !== 'string') return null;
  const get = (k) => {
    const m = raw.match(new RegExp(`${k}=(\\d+)`));
    return m ? Number(m[1]) : null;
  };
  const limit = get('limit');
  const remaining = get('remaining');
  if (limit == null || remaining == null) return null;
  return { limit, remaining, reset: get('reset') };
};

const minutesText = (sec) => {
  const m = Math.max(1, Math.ceil((sec || 0) / 60));
  return `${m} ${plural(m, ['минуту', 'минуты', 'минут'])}`;
};

const formatClock = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null); // { kind: 'credentials'|'locked'|'other', text }
  const [loading, setLoading] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [rateInfo, setRateInfo] = useState(null);
  const [lockLeft, setLockLeft] = useState(0); // секунды до снятия блокировки (429)
  const { login } = useAuth();
  const navigate = useNavigate();
  const lastResponse = useRef(null);
  const usernameRef = useRef(null);

  // Ловим статус и заголовки ответа /auth/login: AuthContext отдаёт только текст ошибки.
  useEffect(() => {
    const isLogin = (cfg) => typeof cfg?.url === 'string' && /\/auth\/login$/.test(cfg.url);
    const id = axios.interceptors.response.use(
      (res) => {
        if (isLogin(res.config)) lastResponse.current = res;
        return res;
      },
      (err) => {
        if (isLogin(err.config)) lastResponse.current = err.response || null;
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    if (lockLeft <= 0) return undefined;
    const t = setTimeout(() => setLockLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [lockLeft]);

  useEffect(() => {
    if (lockLeft === 0 && error?.kind === 'locked') setError(null);
  }, [lockLeft, error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockLeft > 0) return;
    const u = username.trim();
    if (!u || !password) {
      setError({ kind: 'empty', text: !u ? 'Введите логин' : 'Введите пароль' });
      return;
    }
    setError(null);
    setLoading(true);
    lastResponse.current = null;
    const result = await login(u, password);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
      return;
    }
    const res = lastResponse.current;
    const status = res?.status;
    setRateInfo(parseRateLimit(res?.headers));
    if (status === 429) {
      const retry = Number(res?.headers?.['retry-after']) || parseRateLimit(res?.headers)?.reset || 15 * 60;
      setLockLeft(retry);
      setError({ kind: 'locked', text: `Слишком много попыток входа. Попробуйте через ${minutesText(retry)} или восстановите пароль.` });
    } else if (status === 400) {
      setError({ kind: 'credentials' });
      setPassword('');
    } else {
      setError({ kind: 'other', text: cleanMessage(status >= 500 ? '' : result.message, 'Не удалось войти. Попробуйте ещё раз через минуту.') });
    }
  };

  const onPasswordKey = (e) => {
    if (typeof e.getModifierState === 'function') setCapsLock(e.getModifierState('CapsLock'));
  };

  const credErr = error?.kind === 'credentials';
  const remaining = rateInfo?.remaining;
  // Предупреждаем о блокировке, только если сервер прислал реальный счётчик и попыток осталось мало
  const showRemaining = credErr && remaining != null && remaining <= 5;
  const lockMinutes = rateInfo?.reset ? minutesText(rateInfo.reset) : '15 минут';

  return (
    <AuthLayout aside={ASIDE}>
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <AuthHeading title="Вход">Рады видеть снова</AuthHeading>

        {credErr && (
          <div className="auth-alert" role="alert" id="login-err">
            <Icon name="alert" size={20} strokeWidth={2} />
            <div className="auth-alert-body">
              <span className="auth-alert-title">Неверный логин или пароль</span>
              <span className="auth-alert-text">
                Проверьте раскладку и Caps Lock.
                {showRemaining && (remaining > 0
                  ? ` Осталось ${remaining} ${plural(remaining, ['попытка', 'попытки', 'попыток'])}, затем вход будет приостановлен на ${lockMinutes}.`
                  : ` Попытки закончились — следующий вход будет приостановлен на ${lockMinutes}.`)}
              </span>
            </div>
          </div>
        )}
        {error && (error.kind === 'locked' || error.kind === 'other') && (
          <div className="auth-alert" role="alert" id="login-err">
            <Icon name={error.kind === 'locked' ? 'lock' : 'alert'} size={20} strokeWidth={2} />
            <div className="auth-alert-body">
              <span className="auth-alert-title">{error.kind === 'locked' ? 'Вход временно приостановлен' : 'Не удалось войти'}</span>
              <span className="auth-alert-text">{error.text}</span>
            </div>
          </div>
        )}

        <div className="auth-field">
          <label htmlFor="login-username" className="ui-label">Логин</label>
          <Input
            ref={usernameRef}
            id="login-username"
            type="text"
            value={username}
            onChange={(e) => { setUsername(e.target.value); if (error?.kind === 'empty') setError(null); }}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            disabled={loading}
            aria-invalid={credErr || (error?.kind === 'empty' && !username.trim()) || undefined}
            aria-describedby={credErr ? 'login-err' : undefined}
          />
          {error?.kind === 'empty' && !username.trim() && <FieldNote>{error.text}</FieldNote>}
        </div>

        <div className="auth-field">
          <div className="auth-label-row">
            <label htmlFor="login-password" className="ui-label">Пароль</label>
            <Link to="/forgot-password">Забыли пароль?</Link>
          </div>
          <PasswordInput
            id="login-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (error?.kind === 'empty') setError(null); }}
            onKeyUp={onPasswordKey}
            onKeyDown={onPasswordKey}
            autoComplete="current-password"
            disabled={loading}
            invalid={credErr || (error?.kind === 'empty' && !!username.trim() && !password)}
            aria-invalid={credErr || undefined}
            aria-describedby={[credErr && 'pw-err', credErr && 'login-err', capsLock && 'pw-caps'].filter(Boolean).join(' ') || undefined}
          />
          {credErr && <FieldNote id="pw-err">Неверный логин или пароль</FieldNote>}
          {error?.kind === 'empty' && !!username.trim() && !password && <FieldNote>{error.text}</FieldNote>}
          {capsLock && <span id="pw-caps" className="ui-field-hint">Включён Caps Lock</span>}
        </div>

        <Button type="submit" variant="primary" size="lg" block loading={loading} disabled={lockLeft > 0}>
          {lockLeft > 0 ? `Войти можно через ${formatClock(lockLeft)}` : 'Войти'}
        </Button>

        {credErr && rateInfo && (
          <div className="auth-meta">
            <Icon name="clock" size={16} strokeWidth={2} />
            Попытка {rateInfo.limit - rateInfo.remaining} из {rateInfo.limit}. Не помните пароль? <Link to="/forgot-password">Восстановить</Link>
          </div>
        )}
        {error?.kind === 'locked' && (
          <div className="auth-meta">
            <Icon name="clock" size={16} strokeWidth={2} />
            Не помните пароль? <Link to="/forgot-password">Восстановить</Link>
          </div>
        )}

        <div className="auth-switch">Нет аккаунта? <Link to="/register">Создать</Link></div>
      </form>
    </AuthLayout>
  );
};

export default Login;
